import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { GRANT_MONTHS, NOT_ELIGIBLE, REGISTER_CREDITS } from '@/lib/nonprofit'
import { tiers } from '@/lib/plans'

// OpenSpec 136 task 7.2. Claims rule: state only what the server does. Pro limits come from
// `plans.ts`, the term from `GRANT_MONTHS`, and the sources named are the ones the server imports.

const POST_URL = 'https://uptrack.app/blog/free-pro-for-nonprofits'
const TITLE = 'Uptrack Pro Is Free for Nonprofits and Charities'
const DESCRIPTION = `Registered nonprofits and charities get Uptrack Pro free for ${GRANT_MONTHS} months. It starts at once when your email domain is on our lists, or after a short review.`

const PRO = tiers.find((t) => t.plan === 'pro')
const PRO_FEATURES = (PRO?.features ?? []).filter((f) => f.included).map((f) => f.name)

export const Route = createFileRoute('/_site/blog/free-pro-for-nonprofits')({
  component: FreeProForNonprofitsPage,
  head: () => ({
    meta: [
      { title: `${TITLE} — Uptrack` },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: `${TITLE} — Uptrack` },
      { property: 'og:description', content: `Pro free for ${GRANT_MONTHS} months for registered nonprofits and charities. It renews while you still qualify.` },
      { property: 'og:image', content: 'https://uptrack.app/og/blog-free-pro-for-nonprofits.png' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: POST_URL },
      { property: 'article:published_time', content: '2026-10-27' },
    ],
    links: [{ rel: 'canonical', href: POST_URL }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: TITLE,
          description: DESCRIPTION,
          datePublished: '2026-10-27',
          dateModified: '2026-10-27',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app', logo: { '@type': 'ImageObject', url: 'https://uptrack.app/uptrack-logo-header.png' } },
          mainEntityOfPage: POST_URL,
        }),
      },
    ],
  }),
})

const p = 'mb-4 text-lg leading-[1.7] text-body'
const link = 'text-primary hover:underline'
const list = 'mb-4 list-disc space-y-2 pl-6 text-lg leading-[1.7] text-body'

function FreeProForNonprofitsPage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="mb-3 text-sm text-muted-foreground">October 27, 2026</p>
      <h1 className="font-sans mb-6 text-4xl font-semibold">Uptrack Pro is free for nonprofits and charities</h1>

      <section className="mb-12">
        <p className={p}>
          A charity's donation page going down on a campaign day costs more than a monitoring bill ever will.
          So from today, registered nonprofits and charities get Uptrack Pro free for {GRANT_MONTHS} months.
          It renews each year while you still qualify.
        </p>
        <p className={p}>
          For most organizations it takes one click. If your email domain is on one of our lists, you claim Pro in
          Settings, Billing, and it starts at once. If it is not, you send a short application and we check it by hand.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">What you get</h2>
        <p className={p}>The same Pro plan that others pay {PRO ? `$${PRO.monthlyPrice}/mo` : ''} for. Nothing is held back:</p>
        <ul className={list}>
          {PRO_FEATURES.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">How to claim it</h2>
        <ol className="mb-4 list-decimal space-y-2 pl-6 text-lg leading-[1.7] text-body">
          <li>Sign up for Uptrack, or sign in, as an owner or admin of your organization.</li>
          <li>Make sure a member has a confirmed email address on your organization's own domain. An address at a free mail provider such as Gmail does not count.</li>
          <li>Open <strong>Settings → Billing</strong>. The Nonprofit card shows each of your domains and whether it qualifies.</li>
          <li>Press <strong>Claim free Pro</strong>. Your plan changes to Pro right away.</li>
        </ol>
        <p className={p}>A domain qualifies at once when it is on one of these lists:</p>
        <ul className={list}>
          <li>Any <strong>.ngo</strong> or <strong>.ong</strong> domain.</li>
          <li>The website of a charity in the <strong>UK Charity Commission</strong> register.</li>
          <li>The website of a charity in the <strong>Australian ACNC</strong> register.</li>
          <li>Our own list of nonprofits.</li>
        </ul>
        <p className={p}>
          We download the two registers once a week. One organization holds free Pro for each domain.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Not on a list? Apply</h2>
        <p className={p}>
          Most countries have no register we can download. Germany, for example, has no bulk download of its register of
          tax-exempt organizations. If your domain is not listed, the same card shows a short form: country, legal name,
          the name of your register, your registry or tax number, and a link to your public register entry if there is one.
        </p>
        <p className={p}>
          We check the number against the public register and email you our decision. We ask for no documents, and we send
          your application to no one else.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Who it is not meant for</h2>
        <ul className={list}>
          {NOT_ELIGIBLE.map((who) => (
            <li key={who}>{who}</li>
          ))}
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Renewal and the end of a grant</h2>
        <p className={p}>
          In the last 30 days of a grant, Uptrack checks again. If your domain still qualifies and a member still
          has a confirmed email address on it, the grant runs for another {GRANT_MONTHS} months without any action from you.
        </p>
        <p className={p}>
          If it no longer qualifies, the owners and admins get an email 30 days and 7 days before the end, saying what will pause.
          After the end, the organization moves to the plan of its subscription, or to the Free plan if it has none.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Already paying for Pro?</h2>
        <p className={p}>
          You can claim the grant too. Pro then continues through the grant, and you can cancel the paid subscription where
          you subscribed. The paid plan stays until the end of its billing period. If you need more than Pro, you can buy Team
          on top of the grant. If you cancel Team later, you go back to Pro while the grant lasts.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Why we do this</h2>
        <p className={p}>
          Pro already costs {PRO ? `$${PRO.monthlyPrice}/mo` : 'little'}, so price is rarely what stops a nonprofit.
          We do it because nonprofits share tools with each other. If Uptrack keeps your site up, tell another organization about it.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Register data</h2>
        <p className={p}>
          {REGISTER_CREDITS.uk}{' '}
          <a href={REGISTER_CREDITS.ukLicenceUrl} className={link} target="_blank" rel="noopener noreferrer">Licence</a>.{' '}
          {REGISTER_CREDITS.acnc}{' '}
          <a href={REGISTER_CREDITS.acncLicenceUrl} className={link} target="_blank" rel="noopener noreferrer">Licence</a>.
        </p>
        <p className={p}>
          The full rules are in the <Link to="/terms" className={link}>terms</Link>, and the short version is on the{' '}
          <Link to="/for/nonprofits" className={link}>nonprofits page</Link>.
        </p>
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Pro is free for {GRANT_MONTHS} months</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          Sign up, then claim the nonprofit grant in Settings, Billing.
        </p>
        <Button asChild size="lg">
          <Link to="/for/nonprofits">
            Free Pro for nonprofits <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </div>
    </article>
  )
}
